import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getHolidays, approveHoliday, rejectHoliday, deleteHoliday } from '../../../api/services/Candidate/holiday';
import { QK } from '../../../base/qk';
import ConfirmModal from '../../../components/ConfirmModal';
import DataTable from '../../../components/Table/DataTable';

export default function HolidayHistory({ candidateId, selectedYear }) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [dialogType, setDialogType] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { data: holidaysData, isLoading } = useQuery(
    [QK.holidays, candidateId, page, selectedYear],
    () => getHolidays(candidateId, page, selectedYear),
    {
      enabled: !!candidateId,
      staleTime: 5 * 60 * 1000,
    }
  );

  const approveMutation = useMutation(approveHoliday, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.holidays, candidateId]);
      queryClient.invalidateQueries([QK.holidaySummary, candidateId]);
      queryClient.invalidateQueries([QK.holidayBalance, candidateId]);
      setSnackbarMessage('Holiday approved successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDialog(false);
    },
    onError: (error) => {
      setSnackbarMessage(error.response?.data?.message || 'Failed to approve holiday');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const rejectMutation = useMutation(rejectHoliday, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.holidays, candidateId]);
      queryClient.invalidateQueries([QK.holidaySummary, candidateId]);
      queryClient.invalidateQueries([QK.holidayBalance, candidateId]);
      setSnackbarMessage('Holiday rejected successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDialog(false);
    },
    onError: (error) => {
      setSnackbarMessage(error.response?.data?.message || 'Failed to reject holiday');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const deleteMutation = useMutation(deleteHoliday, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.holidays, candidateId]);
      queryClient.invalidateQueries([QK.holidaySummary, candidateId]);
      queryClient.invalidateQueries([QK.holidayBalance, candidateId]);
      setSnackbarMessage('Holiday deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDialog(false);
    },
    onError: (error) => {
      setSnackbarMessage(error.response?.data?.message || 'Failed to delete holiday');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const isLoadingAction = approveMutation.isLoading || rejectMutation.isLoading || deleteMutation.isLoading;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getDialogConfig = (type) => {
    switch (type) {
      case 'approve':
        return {
          title: 'Approve Holiday Request',
          body: `Are you sure you want to approve this holiday request for ${selectedHoliday?.duration} days?`,
          confirmText: 'Approve',
          isDelete: false,
        };
      case 'reject':
        return {
          title: 'Reject Holiday Request',
          body: `Are you sure you want to reject this holiday request for ${selectedHoliday?.duration} days?`,
          confirmText: 'Reject',
          isDelete: false,
        };
      case 'delete':
        return {
          title: 'Delete Holiday Request',
          body: `Are you sure you want to delete this holiday request permanently? This action cannot be undone.`,
          confirmText: 'Delete',
          isDelete: true,
        };
      default:
        return {
          title: 'Confirm Action',
          body: 'Are you sure you want to proceed?',
          confirmText: 'Confirm',
          isDelete: false,
        };
    }
  };

  const openConfirmDialog = (holiday, type) => {
    setSelectedHoliday(holiday);
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleConfirmAction = () => {
    if (dialogType === 'approve') {
      approveMutation.mutate(selectedHoliday.id);
    } else if (dialogType === 'reject') {
      rejectMutation.mutate(selectedHoliday.id);
    } else if (dialogType === 'delete') {
      deleteMutation.mutate(selectedHoliday.id);
    }
  };

  const handleView = (id) => {
    // View logic here
  };

  const headers = [
    { key: 'date_from', label: 'From' },
    { key: 'date_to', label: 'To' },
    { key: 'duration', label: 'Duration' },
    { key: 'half_day', label: 'Half Day' },
    { key: 'status', label: 'Status' },
  ];

  const renderCell = (item, header) => {
    if (header.key === 'date_from') {
      return (
        <Typography variant="body2" color="#1A1A2E">
          {item.date_from}
        </Typography>
      );
    }

    if (header.key === 'date_to') {
      return (
        <Typography variant="body2" color="#1A1A2E">
          {item.date_to}
        </Typography>
      );
    }

    if (header.key === 'duration') {
      return (
        <Typography variant="body2" fontWeight={600} color="#1A1A2E">
          {item.duration} days
        </Typography>
      );
    }

    if (header.key === 'half_day') {
      return item.half_day_from || item.half_day_to ? (
        <Chip
          label="Half Day"
          size="small"
          sx={{
            height: 20,
            fontSize: '0.6rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette.info.main, 0.12),
            color: theme.palette.info.main,
          }}
        />
      ) : (
        <Chip
          label="Full Day"
          size="small"
          variant="outlined"
          sx={{
            height: 20,
            fontSize: '0.6rem',
            fontWeight: 600,
            borderColor: '#E2E8F0',
            color: '#475467',
          }}
        />
      );
    }

    if (header.key === 'status') {
      return (
        <Chip
          label={getStatusLabel(item.status)}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette[getStatusColor(item.status)].main, 0.12),
            color: theme.palette[getStatusColor(item.status)].main,
          }}
        />
      );
    }

    return item[header.key] || '-';
  };

  const customActions = (item) => (
    <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
      {item.status === 'pending' && (
        <>
          <Tooltip title="Approve" arrow>
            <IconButton
              size="small"
              onClick={() => openConfirmDialog(item, 'approve')}
              sx={{
                color: '#667085',
                p: 0.5,
                '&:hover': {
                  color: '#22C55E',
                  bgcolor: alpha('#22C55E', 0.06),
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject" arrow>
            <IconButton
              size="small"
              onClick={() => openConfirmDialog(item, 'reject')}
              sx={{
                color: '#667085',
                p: 0.5,
                '&:hover': {
                  color: '#EF4444',
                  bgcolor: alpha('#EF4444', 0.06),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="View" arrow>
        <IconButton
          size="small"
          onClick={() => handleView(item.id)}
          sx={{
            color: '#667085',
            p: 0.5,
            '&:hover': {
              color: '#2469F0',
              bgcolor: alpha('#2469F0', 0.06),
            },
          }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => openConfirmDialog(item, 'delete')}
          sx={{
            color: '#667085',
            p: 0.5,
            '&:hover': {
              color: '#EF4444',
              bgcolor: alpha('#EF4444', 0.06),
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const holidays = holidaysData?.results || [];

  if (holidays.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No holiday records found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DataTable
        data={holidays}
        headers={headers}
        renderCell={renderCell}
        customActions={customActions}
        loading={isLoading}
      />

      {holidaysData?.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={holidaysData.total_pages || 1}
            page={page}
            onChange={(e, v) => setPage(v)}
            size="small"
            color="primary"
          />
        </Box>
      )}

      <ConfirmModal
        show={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={handleConfirmAction}
        title={getDialogConfig(dialogType).title}
        body={getDialogConfig(dialogType).body}
        isLoading={isLoadingAction}
        confirmText={getDialogConfig(dialogType).confirmText}
        cancelText="Cancel"
        isDelete={getDialogConfig(dialogType).isDelete}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}